import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { Search, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";

const TIERS = ["all", "free", "premium", "platinum"];

export default function UsersScreen() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.get(`/users?q=${encodeURIComponent(q)}&tier=${tier}`);
      setUsers(data.users || []);
    } catch {} finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const exportCsv = () => {
    const rows = [["email", "name", "tier", "country", "created_at"]];
    users.forEach((u) => rows.push([u.email, u.name, u.tier, u.country || "", u.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `users_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-shadow-green">User Management</h1>
          <p className="text-muted-soft text-sm">{users.length} user{users.length !== 1 && "s"} shown</p>
        </div>
        <button onClick={exportCsv} className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="users-export">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="glass-card flex flex-col sm:flex-row gap-3 p-3 mb-5">
        <div className="flex items-center gap-2 flex-1 px-3">
          <Search size={16} className="text-[#4CAF50]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="flex-1 bg-transparent outline-none text-white placeholder-white/30 text-sm py-2"
            data-testid="users-search"
          />
        </div>
        <div className="flex gap-2 px-1 overflow-x-auto">
          {TIERS.map((t) => (
            <button
              key={t}
              onClick={() => setTier(t)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize whitespace-nowrap ${
                tier === t ? "bg-[rgba(76,175,80,0.18)] border border-[rgba(76,175,80,0.5)] text-white" : "border border-white/15 text-white/60"
              }`}
              data-testid={`users-filter-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-wider text-muted-soft border-b border-white/5">
          <div className="col-span-4">User</div>
          <div className="col-span-3">Country</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-1" />
        </div>
        {busy && <div className="p-6 text-center"><span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" /></div>}
        {!busy && users.length === 0 && <div className="p-10 text-center text-soft text-sm">No users match.</div>}
        {users.map((u) => (
          <div
            key={u.user_id}
            onClick={() => navigate(`/admin/users/${u.user_id}`)}
            className="grid grid-cols-12 px-4 py-3 items-center text-sm hover:bg-white/3 cursor-pointer border-b border-white/3 last:border-b-0 transition-colors"
            data-testid={`user-row-${u.user_id}`}
          >
            <div className="col-span-4 min-w-0">
              <p className="font-semibold truncate">{u.name}</p>
              <p className="text-[11px] text-muted-soft truncate">{u.email}</p>
            </div>
            <div className="col-span-3 text-soft truncate">{u.country || "—"}</div>
            <div className="col-span-2">
              <span className={`glass-pill ${u.tier === "platinum" ? "purple" : u.tier === "premium" ? "amber" : ""}`}>
                {u.tier}
              </span>
            </div>
            <div className="col-span-2 text-muted-soft text-[12px]">
              {new Date(u.created_at).toLocaleDateString()}
            </div>
            <div className="col-span-1 flex justify-end">
              <ChevronRight size={14} className="text-white/40" />
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
